<template>
  <div v-if="ship" :key="ship.id" class="ship-info-page">
    <!-- ====== Hero ====== -->
    <div class="info-hero">
      <div class="hero-bg">
        <svg viewBox="0 0 800 200" preserveAspectRatio="none" class="hero-svg">
          <circle cx="100" cy="100" r="180" fill="rgba(255,255,255,0.05)" />
          <circle cx="650" cy="40" r="150" fill="rgba(8,145,178,0.22)" />
          <path
            d="M 0 150 Q 200 100 400 130 T 800 110"
            stroke="rgba(255,255,255,0.10)"
            stroke-width="1"
            fill="none"
          />
          <path
            d="M 0 170 Q 220 140 420 160 T 800 140"
            stroke="rgba(255,255,255,0.05)"
            stroke-width="1"
            fill="none"
          />
        </svg>
        <svg viewBox="0 0 64 64" class="compass-deco" aria-hidden="true">
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke="rgba(255,255,255,0.18)"
            stroke-width="1.2"
          />
          <circle
            cx="32"
            cy="32"
            r="20"
            fill="none"
            stroke="rgba(255,255,255,0.10)"
            stroke-width="1"
          />
          <circle cx="32" cy="32" r="3" fill="rgba(255,255,255,0.8)" />
          <polygon points="32,10 34,32 32,32" fill="rgba(255,255,255,0.85)" />
          <polygon points="32,54 30,32 32,32" fill="rgba(165,243,252,0.55)" />
        </svg>
      </div>

      <button class="back-btn" @click="router.back()">
        <el-icon><ArrowLeft /></el-icon>
        <span>返回</span>
      </button>

      <div class="hero-main">
        <div class="hero-avatar">
          <el-icon><Ship /></el-icon>
        </div>
        <div class="hero-text">
          <div class="hero-tag">SHIP DETAIL · 船舶档案</div>
          <h1 class="hero-title">{{ ship.ship.name }}</h1>
          <div class="hero-meta">
            <span class="meta-chip">
              <span class="meta-key">ID</span>
              <span class="meta-val" :title="ship.ship.id">{{
                shortId(ship.ship.id)
              }}</span>
            </span>
            <span class="meta-chip">
              <span class="meta-key">集团</span>
              <span class="meta-val">{{ ship.orgName || "—" }}</span>
            </span>
          </div>
        </div>
        <div class="hero-actions">
          <button
            class="action-btn action-primary"
            :disabled="!shipWriteAuthorization"
            @click="handleCreateUpgradePlanClick"
          >
            <el-icon><Promotion /></el-icon>
            <span>{{ $t("SI.createUpgradePlan") }}</span>
          </button>
          <button
            class="action-btn action-secondary"
            :disabled="!shipWriteAuthorization"
            @click="handleOperationManageProtocolClick"
          >
            <el-icon><Setting /></el-icon>
            <span>{{ $t("SI.operationManageProtocol") }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ====== Tabs ====== -->
    <div class="info-tabs-wrap">
      <el-tabs v-model="activeTab" class="info-tabs">
        <el-tab-pane :label="$t('SI.info')" name="info">
          <div class="info-section">
            <div class="section-title">
              <span class="title-bar"></span>
              <span>基础信息</span>
            </div>
            <div class="info-grid">
              <div class="info-card">
                <div class="info-icon">
                  <el-icon><Ship /></el-icon>
                </div>
                <div class="info-body">
                  <span class="info-label">{{ $t("ship.id") }}</span>
                  <span class="info-value" :title="ship.ship.id">{{
                    shortId(ship.ship.id)
                  }}</span>
                </div>
              </div>
              <div class="info-card">
                <div class="info-icon info-icon-cyan">
                  <el-icon><Calendar /></el-icon>
                </div>
                <div class="info-body">
                  <span class="info-label">{{ $t("ship.createtime") }}</span>
                  <span class="info-value">{{
                    formatTime(ship.ship.createtime)
                  }}</span>
                </div>
              </div>
              <div class="info-card">
                <div class="info-icon info-icon-green">
                  <el-icon><Calendar /></el-icon>
                </div>
                <div class="info-body">
                  <span class="info-label">{{ $t("ship.deliveryDate") }}</span>
                  <span class="info-value">{{
                    formatTime(ship.deliveryDate)
                  }}</span>
                </div>
              </div>
              <div class="info-card">
                <div class="info-icon info-icon-purple">
                  <el-icon><Refresh /></el-icon>
                </div>
                <div class="info-body">
                  <span class="info-label">更新时间</span>
                  <span class="info-value">{{
                    formatTime(ship.ship.updatetime)
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="info-section">
            <div class="section-title">
              <span class="title-bar"></span>
              <span>船舶资料</span>
            </div>

            <div class="description-card">
              <div class="desc-header">
                <el-icon><Document /></el-icon>
                <span class="desc-label">描述</span>
              </div>
              <div class="desc-body">
                {{ ship.ship.description || "暂无描述" }}
              </div>
            </div>

            <div class="info-grid info-grid-2">
              <div class="info-card">
                <div class="info-icon info-icon-amber">
                  <el-icon><OfficeBuilding /></el-icon>
                </div>
                <div class="info-body">
                  <span class="info-label">所属集团</span>
                  <span class="info-value">{{ ship.orgName || "—" }}</span>
                </div>
              </div>
              <div class="info-card">
                <div class="info-icon info-icon-rose">
                  <el-icon><Clock /></el-icon>
                </div>
                <div class="info-body">
                  <span class="info-label">在册时长</span>
                  <span class="info-value">{{ tenureLabel }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!shipWriteAuthorization" class="auth-warning">
            <el-icon><Warning /></el-icon>
            <span
              >当前账户对该船舶无写入权限，操作按钮已禁用。可前往集团详情重新申请授权。</span
            >
          </div>
        </el-tab-pane>

        <el-tab-pane :label="$t('SI.deviceInfo')" name="device" lazy>
          <div class="device-card">
            <h3 class="device-title">{{ $t("SI.customDeviceTopoGraph") }}</h3>
            <div class="device-chart">
              <ShipTopoGraph :ship-id="shipId" />
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane :label="$t('SI.map')" name="map" :disabled="true" />
      </el-tabs>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { validateShipAuthorizationApi, getShipInfoApi } from "@/api/productApi";
import router from "@/router";
import { ShipVO } from "@/types/product";
import { ElMessage } from "element-plus";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Document,
  OfficeBuilding,
  Promotion,
  Refresh,
  Setting,
  Ship,
  Warning,
} from "@element-plus/icons-vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import ShipTopoGraph from "@/components/product/ShipTopoGraph.vue";

const route = useRoute();
const activeTab = ref<string>("info");

const shipId = computed(() => {
  return route.query.shipId as string;
});

watch(shipId, () => {
  getShipInfo();
});

const ship = ref<ShipVO>();

const getShipInfo = async () => {
  const response = await getShipInfoApi(shipId.value);
  if (response) {
    ship.value = response;
  } else {
    router.back();
  }
};

// 获取船舶写入授权情况
const shipWriteAuthorization = ref<boolean>(false);
const validateShipAuthorization = async () => {
  const readAuthority = await validateShipAuthorizationApi(shipId.value, 15);
  const writeAuthority = await validateShipAuthorizationApi(shipId.value, 16);
  if (!readAuthority && !writeAuthority) {
    router.replace({ name: "overall" });
  }
  if (writeAuthority) {
    shipWriteAuthorization.value = writeAuthority;
  }
};

// 创建升级计划按钮点击事件
const handleCreateUpgradePlanClick = () => {
  validateShipAuthorization();
  if (!shipWriteAuthorization.value) {
    ElMessage.error("用户对当前船舶的写入权限已过期，请重新申请");
  }
  router.push({
    name: "createUpgradePlan",
    query: {
      shipId: shipId.value,
    },
  });
};

// 运营管理协议点击事件
const handleOperationManageProtocolClick = () => {
  validateShipAuthorization();
  if (!shipWriteAuthorization.value) {
    ElMessage.error("用户对当前船舶的写入权限已过期，请重新申请");
  }
  router.push({
    name: "shipProtocolTemplate",
    query: {
      shipId: shipId.value,
      shipName: ship.value?.ship.name || "",
    },
  });
};

const formatTime = (t?: string | number) => {
  if (!t) return "—";
  const d = typeof t === "string" ? Date.parse(t) : t;
  if (isNaN(d)) return String(t);
  return new Date(d).toLocaleString("zh-CN", { hour12: false });
};

const shortId = (id?: string) => {
  if (!id) return "—";
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
};

/**
 * 在册时长（基于创建时间）
 */
const tenureLabel = computed(() => {
  const t = ship.value?.createtime;
  if (!t) return "—";
  const created = new Date(t).getTime();
  if (isNaN(created)) return "—";
  const diff = Date.now() - created;
  if (diff < 0) return "—";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 30) return `${days} 天`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} 个月`;
  const years = Math.floor(months / 12);
  const restMonths = months % 12;
  return restMonths ? `${years} 年 ${restMonths} 个月` : `${years} 年`;
});

onMounted(() => {
  getShipInfo();
  validateShipAuthorization();
});
</script>

<style lang="less" scoped>
.ship-info-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 28px 32px;
  box-sizing: border-box;
  min-height: 100vh;
  background: radial-gradient(
      at 10% 0%,
      rgba(8, 145, 178, 0.06) 0px,
      transparent 45%
    ),
    radial-gradient(at 90% 100%, rgba(13, 148, 136, 0.05) 0px, transparent 45%),
    linear-gradient(180deg, #f4f8fb 0%, #e7eef5 100%);
  background-attachment: fixed;
}

/* ====== Hero ====== */
.info-hero {
  position: relative;
  background: linear-gradient(135deg, #0c2461 0%, #1e3799 45%, #0a3d62 100%);
  color: #fff;
  border-radius: 20px;
  padding: 28px 36px;
  overflow: hidden;
  box-shadow: 0 16px 36px rgba(12, 36, 97, 0.32);

  .hero-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;

    .hero-svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }

    .compass-deco {
      position: absolute;
      right: 28%;
      top: 50%;
      width: 200px;
      height: 200px;
      transform: translateY(-50%);
      opacity: 0.4;
      animation: spinSlow 50s linear infinite;
    }
  }

  .back-btn {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 999px;
    color: rgba(224, 242, 254, 0.9);
    font-size: 12px;
    cursor: pointer;
    backdrop-filter: blur(8px);
    margin-bottom: 14px;
    transition: background 0.2s ease, transform 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateX(-2px);
    }

    .el-icon {
      font-size: 13px;
    }
  }

  .hero-main {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  .hero-avatar {
    flex-shrink: 0;
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%);
    color: #0c2461;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  .hero-text {
    flex: 1;
    min-width: 220px;

    .hero-tag {
      display: inline-block;
      padding: 3px 10px;
      font-size: 10px;
      letter-spacing: 3px;
      font-weight: 600;
      color: #a5f3fc;
      background: rgba(165, 243, 252, 0.14);
      border: 1px solid rgba(165, 243, 252, 0.32);
      border-radius: 999px;
      margin-bottom: 8px;
    }

    .hero-title {
      font-size: 28px;
      font-weight: 800;
      color: #fff;
      margin: 0 0 10px;
      letter-spacing: 0.8px;
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      line-height: 1.2;
    }

    .hero-meta {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;

      .meta-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 8px;
        backdrop-filter: blur(6px);

        .meta-key {
          font-size: 11px;
          letter-spacing: 1.5px;
          color: #a5f3fc;
          font-weight: 600;
        }

        .meta-val {
          font-size: 12px;
          color: #fff;
          font-weight: 500;
          font-variant-numeric: tabular-nums;
        }
      }
    }
  }

  .hero-actions {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;

    .action-btn {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 20px;
      border: none;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.3s ease, opacity 0.2s ease;

      .el-icon {
        font-size: 15px;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
      }

      &:hover:not(:disabled) {
        transform: translateY(-1px);
      }

      &.action-primary {
        color: #0c2461;
        background: linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12),
          inset 0 1px 0 rgba(255, 255, 255, 0.6);

        &:hover:not(:disabled) {
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
        }
      }

      &.action-secondary {
        color: #fff;
        background: rgba(255, 255, 255, 0.14);
        border: 1px solid rgba(255, 255, 255, 0.3);
        backdrop-filter: blur(8px);

        &:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.24);
        }
      }
    }
  }
}

/* ====== Tabs ====== */
.info-tabs-wrap {
  background: #fff;
  border-radius: 16px;
  padding: 4px 18px 22px;
  box-shadow: 0 4px 16px rgba(8, 145, 178, 0.06), 0 1px 2px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(8, 145, 178, 0.08);

  :deep(.info-tabs) {
    .el-tabs__nav-wrap::after {
      background: rgba(8, 145, 178, 0.12);
    }

    .el-tabs__item {
      height: 48px;
      line-height: 48px;
      font-size: 14px;
      font-weight: 600;
      color: #64748b;
      padding: 0 16px;

      &:hover {
        color: #0e7490;
      }

      &.is-active {
        color: #0e7490;
        font-weight: 700;
      }
    }

    .el-tabs__active-bar {
      background: linear-gradient(90deg, #0e7490 0%, #0891b2 100%) !important;
      height: 3px !important;
      border-radius: 2px;
    }
  }
}

/* ====== 信息分区 ====== */
.info-section {
  padding-top: 18px;

  & + .info-section {
    margin-top: 18px;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 14px;
  letter-spacing: 0.4px;

  .title-bar {
    display: inline-block;
    width: 4px;
    height: 14px;
    background: linear-gradient(180deg, #0e7490 0%, #0891b2 100%);
    border-radius: 2px;
  }
}

/* ====== 信息网格 ====== */
.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  &.info-grid-2 {
    grid-template-columns: repeat(2, 1fr);
  }
}

.info-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid rgba(8, 145, 178, 0.08);
  border-radius: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(8, 145, 178, 0.08);
  }

  .info-icon {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, #0c2461 0%, #0891b2 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    box-shadow: 0 4px 10px rgba(8, 145, 178, 0.28);

    &.info-icon-cyan {
      background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
    }
    &.info-icon-green {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    }
    &.info-icon-amber {
      background: linear-gradient(135deg, #f59e0b 0%, #b45309 100%);
    }
    &.info-icon-purple {
      background: linear-gradient(135deg, #6366f1 0%, #4338ca 100%);
    }
    &.info-icon-rose {
      background: linear-gradient(135deg, #ec4899 0%, #be185d 100%);
    }
  }

  .info-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .info-label {
      font-size: 12px;
      color: #64748b;
      letter-spacing: 0.3px;
    }

    .info-value {
      font-size: 15px;
      font-weight: 600;
      color: #0f172a;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

/* ====== 描述卡 ====== */
.description-card {
  margin-bottom: 14px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid rgba(8, 145, 178, 0.16);
  border-radius: 12px;

  .desc-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #0e7490;
    letter-spacing: 0.3px;
    font-weight: 600;
    margin-bottom: 10px;

    .el-icon {
      font-size: 14px;
    }
  }

  .desc-body {
    font-size: 14px;
    color: #1e293b;
    line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-word;
  }
}

/* ====== 权限警告 ====== */
.auth-warning {
  margin-top: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.28);
  border-radius: 10px;
  font-size: 13px;
  color: #b45309;

  .el-icon {
    font-size: 16px;
    color: #f59e0b;
  }
}

/* ====== 设备拓扑图卡片 ====== */
.device-card {
  margin-top: 14px;
  background: #fff;
  border: 1px solid rgba(8, 145, 178, 0.08);
  border-radius: 12px;
  padding: 20px;

  .device-title {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 14px;
    letter-spacing: 0.3px;
  }

  .device-chart {
    height: 360px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 10px;
    padding: 12px;
  }
}

@keyframes spinSlow {
  from {
    transform: translateY(-50%) rotate(0deg);
  }
  to {
    transform: translateY(-50%) rotate(360deg);
  }
}

@media (max-width: 1100px) {
  .info-grid {
    grid-template-columns: repeat(2, 1fr);

    &.info-grid-2 {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 640px) {
  .info-grid,
  .info-grid-2 {
    grid-template-columns: 1fr;
  }
  .info-hero {
    padding: 22px;
  }
  .hero-actions {
    width: 100%;
  }
}
</style>
